<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { RouterLink } from 'vue-router';
import EmailHistoryAPI from 'dashboard/api/emailHistory';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ conversationId: { type: [Number, String], required: true } });
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const data = ref(null);
const error = ref(false);
const busy = ref(false);
let revision = 0;
let timer;
let loadedCompletion;
const reloadMessages = async () => {
  const current = revision;
  try {
    await store.dispatch('refreshEmailHistoryMessages', Number(props.conversationId));
  } catch { if (current === revision) error.value = true; }
};
const active = computed(() => ['queued', 'running'].includes(data.value?.sync?.state));
const partial = computed(() => {
  const counts = data.value?.sync?.counts || {};
  return ['skipped', 'oversized', 'ambiguous'].some(key => counts[key] > 0);
});
const stop = () => clearTimeout(timer);
const load = async (action = null) => {
  const current = ++revision;
  stop();
  busy.value = true;
  error.value = false;
  try {
    const response = action
      ? await EmailHistoryAPI.request(props.conversationId, action)
      : await EmailHistoryAPI.status(props.conversationId);
    if (current !== revision) return;
    data.value = response.data;
    const completion = data.value.sync?.state === 'complete' ? data.value.sync.updated_at : null;
    if (completion && completion !== loadedCompletion) {
      await store.dispatch('refreshEmailHistoryMessages', Number(props.conversationId));
      if (current !== revision) return;
      loadedCompletion = completion;
    }
    if (data.value.supported) timer = setTimeout(() => load(), active.value ? 5000 : 30000);
  } catch (e) {
    if (current !== revision) return;
    error.value = true;
    if ([401, 403, 404].includes(e?.response?.status)) data.value = null;
  } finally {
    if (current === revision) busy.value = false;
  }
};
watch([accountId, () => props.conversationId], () => {
  revision += 1;
  stop();
  data.value = null;
  loadedCompletion = null;
  load();
}, { immediate: true });
onBeforeUnmount(() => { revision += 1; stop(); });
</script>

<template>
  <!-- Keep the component mounted for automatic history-message refresh. -->
  <section v-show="false" v-if="data?.supported || error" class="mx-3 mb-3 rounded-xl border border-n-weak p-3 text-sm" data-email-history>
    <p class="mb-2 font-medium text-n-slate-12">{{ $t('BORDERCHAT.EMAIL_HISTORY.TITLE') }}</p>
    <p class="mb-1 text-n-slate-11">{{ $t(`BORDERCHAT.EMAIL_HISTORY.STATE.${data?.sync?.state || 'idle'}`) }}</p>
    <p v-if="data?.sync" class="mb-1 text-xs text-n-slate-10">
      {{ data?.sync.since.slice(0, 10) }} — {{ data?.sync.until.slice(0, 10) }}
    </p>
    <p v-if="data?.sync" class="mb-1 text-xs text-n-slate-11">
      {{ $t('BORDERCHAT.EMAIL_HISTORY.COUNTS', { imported: data?.sync.counts.imported || 0, existing: data?.sync.counts.existing || 0, excluded: data?.sync.counts.excluded || 0 }) }}
    </p>
    <div v-if="data?.placement" class="mb-2 text-xs text-n-slate-11" data-history-placement>
      <p>{{ $t('BORDERCHAT.EMAIL_HISTORY.PLACEMENT', { current: data.placement.current, other: data.placement.other }) }}</p>
      <div v-if="data.placement.conversations.length" class="mt-1 flex flex-wrap gap-x-3 gap-y-1">
        <RouterLink v-for="item in data.placement.conversations" :key="item.display_id"
          :to="`/app/accounts/${accountId}/inbox/${data.placement.inbox_id}/conversations/${item.display_id}`"
          class="text-n-blue-text hover:underline">
          {{ $t('BORDERCHAT.EMAIL_HISTORY.OTHER_CONVERSATION', { id: item.display_id, count: item.count }) }}
        </RouterLink>
      </div>
      <p v-if="data.placement.more">{{ $t('BORDERCHAT.EMAIL_HISTORY.MORE_CONVERSATIONS') }}</p>
    </div>
    <p v-if="data?.sync?.counts.sent_folder_available === false" class="mb-2 text-xs text-n-amber-11">{{ $t('BORDERCHAT.EMAIL_HISTORY.NO_SENT') }}</p>
    <p v-if="data?.sync?.counts.live_pending" class="mb-2 text-xs text-n-slate-10">{{ $t('BORDERCHAT.EMAIL_HISTORY.LIVE_PENDING') }}</p>
    <p v-if="partial" class="mb-2 text-xs text-n-amber-11">{{ $t('BORDERCHAT.EMAIL_HISTORY.PARTIAL') }}</p>
    <p v-if="data?.sync?.state === 'complete'" class="mb-2 text-xs text-n-slate-10">{{ $t('BORDERCHAT.EMAIL_HISTORY.COMPLETE_NOTE') }}</p>
    <p v-if="error" role="alert" class="mb-2 text-xs text-n-ruby-11">{{ $t('BORDERCHAT.EMAIL_HISTORY.ERROR') }}</p>
    <div class="flex flex-wrap gap-2">
      <Button v-if="data?.can_manage && !data?.sync" :label="$t('BORDERCHAT.EMAIL_HISTORY.START')" :disabled="busy" xs @click="load('start')" />
      <Button v-if="data?.can_manage && data?.sync?.state === 'complete'" :label="$t('BORDERCHAT.EMAIL_HISTORY.EXTEND')" :disabled="busy" xs @click="load('extend')" />
      <Button v-if="data?.can_manage && ['failed', 'cancelled'].includes(data?.sync?.state)" :label="$t('BORDERCHAT.EMAIL_HISTORY.RETRY')" :disabled="busy" xs @click="load('retry')" />
      <Button v-if="data?.sync?.state === 'complete'" :label="$t('BORDERCHAT.EMAIL_HISTORY.LOAD_MESSAGES')" :disabled="busy" xs @click="reloadMessages" />
      <Button :label="$t('BORDERCHAT.EMAIL_HISTORY.REFRESH')" :disabled="busy" slate ghost xs @click="load()" />
    </div>
  </section>
</template>
